import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Sparkle, Star } from "@phosphor-icons/react";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/products?featured=true")
      .then(r => setFeatured(Array.isArray(r.data) ? r.data : []))
      .catch(err => console.error("Failed to load featured products:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center noise-overlay overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/9965979/pexels-photo-9965979.jpeg"
            alt="Luxury background"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-6 animate-fade-in font-medium" style={{ animationDelay: '200ms', opacity: 0 }}>
            By La'Daena Londo
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-7xl lg:text-8xl font-light text-[#FDFBF7] tracking-tighter leading-[0.9] animate-fade-in-up" style={{ animationDelay: '400ms', opacity: 0 }}>
            Lead<span className="text-[#D4AF37]">.</span> Serve<span className="text-[#D4AF37]">.</span> Slay<span className="gold-shimmer">&trade;</span>
          </h1>
          <p className="mt-8 text-[#A1A1AA] text-base md:text-lg font-light max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '600ms', opacity: 0 }}>
            Luxury leadership. Soft power. Unforgettable presence. 
            Premium fragrance oils and body butters crafted for the extraordinary.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '800ms', opacity: 0 }}>
            <Link
              to="/shop"
              className="btn-luxury inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-10 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500"
              data-testid="hero-shop-btn"
            >
              Shop Now <ArrowRight size={16} weight="bold" />
            </Link>
            <Link
              to="/shop?category=fragrance"
              className="inline-flex items-center justify-center gap-3 border border-[#D4AF37] text-[#D4AF37] px-10 py-4 text-sm tracking-[0.2em] uppercase font-light hover:bg-[#D4AF37]/10 transition-colors duration-500"
              data-testid="hero-fragrance-btn"
            >
              Discover Scents
            </Link>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-[1px] h-12 bg-gradient-to-b from-transparent to-[#D4AF37]/40" />
        </div>
      </section>

      {/* Editorial Marquee */}
      <section className="py-6 bg-[#0a0a0a] border-y border-white/5 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex gap-32">
          {["marquee-1", "marquee-2", "marquee-3"].map((id) => (
            <div key={id} className="flex items-center gap-32 text-[#D4AF37]/40 text-sm tracking-[0.3em] uppercase font-light">
              <span className="flex items-center gap-3"><Sparkle size={12} /> Luxury Fragrance Oils</span>
              <span className="flex items-center gap-3"><Star size={12} /> Body Butters</span>
              <span className="flex items-center gap-3"><Sparkle size={12} /> Coming Soon: Clothing</span>
              <span className="flex items-center gap-3"><Star size={12} /> Black-Owned Luxury</span>
              <span className="flex items-center gap-3"><Sparkle size={12} /> Handcrafted Excellence</span>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="featured-section">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">Curated Selection</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#FDFBF7] tracking-tight">
              Featured Collection
            </h2>
          </div>
          <Link to="/shop" className="hidden md:flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 tracking-wide uppercase font-light">
            View All <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3].map(i => (
              <div key={i} className="aspect-square bg-[#121212] animate-pulse border border-white/5" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {(Array.isArray(featured) ? featured : []).slice(0, 6).map((product, idx) => (
              <ProductCard key={product.id} product={product} index={idx} />
            ))}
          </div>
        )}
        <div className="mt-12 text-center md:hidden">
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-[#D4AF37] tracking-wide uppercase font-light">
            View All Products <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Brand Story / Bento Grid */}
      <section className="py-24 px-6 lg:px-12 max-w-7xl mx-auto" data-testid="brand-story-section">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Large image */}
          <div className="lg:col-span-3 relative aspect-[4/3] overflow-hidden border border-[rgba(212,175,55,0.1)] bg-[#0f0f0f]">
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_slay-collective/artifacts/6edljsd9_Founder-%20La%27Daena%20Londo.png"
              alt="La'Daena Londo"
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 md:p-12">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">The Founder</p>
              <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#FDFBF7] tracking-tight">
                La'Daena Londo
              </h3>
            </div>
          </div>

          {/* Text blocks */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8 md:p-10 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4 font-medium">Our Philosophy</p>
              <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[#FDFBF7] tracking-tight mb-4">
                Wealth. Power. Confidence.
              </h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed font-light">
                Every product is crafted to make you feel like the extraordinary woman you are. 
                From our luxurious fragrance oils to our decadent body butters, 
                each creation is an invitation to lead, to serve, and to slay.
              </p>
            </div>
            <div className="flex-1 bg-[#121212] border border-[rgba(212,175,55,0.1)] p-8 md:p-10 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4 font-medium">Coming Soon</p>
              <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[#FDFBF7] tracking-tight mb-4">
                The Slay Collection
              </h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed font-light">
                An exclusive clothing line for the woman who commands every room. 
                Premium fabrics. Bold silhouettes. Unapologetic luxury.
              </p>
              <Link to="/shop?category=clothing" className="inline-flex items-center gap-2 text-[#D4AF37] text-sm mt-4 tracking-wide uppercase font-light hover:text-[#E5C158] transition-colors duration-300">
                Explore <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter / CTA */}
      <section className="py-24 px-6 border-t border-white/5" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4 font-medium">Stay Connected</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#FDFBF7] tracking-tight mb-6">
            Join the Inner Circle
          </h2>
          <p className="text-[#A1A1AA] text-sm font-light mb-8 leading-relaxed">
            Be the first to know about new launches, exclusive offers, and the Slay Collection reveal.
          </p>
          <div className="flex flex-col sm:flex-row gap-0">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 bg-[#121212] border-b border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light"
              data-testid="newsletter-email-input"
            />
            <button className="bg-[#D4AF37] text-black px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300" data-testid="newsletter-submit-btn">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
